import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Smile, Frown, Meh, RefreshCw, Loader2, ShieldCheck, ShieldX, FileText, Copy, Check, Download, ChevronDown, ExternalLink, AlertTriangle, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIInsights() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('sentiment');

  return (
    <AppShell active="visibility">
      <div className="min-h-screen pb-24 desktop:pb-8 bg-[#F7F4EF]" data-testid="ai-insights-page">
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <button onClick={() => navigate('/dashboard')} className="p-1 rounded-none hover:bg-[#EDE8DF]">
              <ArrowLeft size={20} style={{ color: '#8C7E6E' }} />
            </button>
            <h1 className="text-2xl font-medium" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>
              AI Insights
            </h1>
          </div>
        </header>

        <div className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl">
          {/* Tab Navigation */}
          <div className="flex gap-1 mb-5 p-1 rounded-none" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
            {[
              { key: 'sentiment', label: 'Sentiment', icon: Smile },
              { key: 'robots', label: 'AI Crawlers', icon: ShieldCheck },
              { key: 'llms', label: 'LLMs.txt', icon: FileText },
            ].map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-none text-[12px] font-medium transition-all"
                  style={{
                    backgroundColor: tab === t.key ? '#FFFFFF' : 'transparent',
                    color: tab === t.key ? '#3C3226' : '#777',
                    boxShadow: tab === t.key ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                  }}
                  data-testid={`tab-${t.key}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === 'sentiment' && <SentimentTab />}
          {tab === 'robots' && <RobotsTab />}
          {tab === 'llms' && <LlmsTxtTab />}
        </div>
      </div>
    </AppShell>
  );
}


function SentimentTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sentiment`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/sentiment/analyze`, {}, { withCredentials: true });
      setData(res.data);
    } catch (e) {}
    setAnalyzing(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: '#D4C9B8' }} /></div>;

  const mentions = data?.mentions || [];
  const summary = data?.summary || {};

  return (
    <div data-testid="sentiment-tab">
      {/* Summary */}
      {data?.has_report && summary.total > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
          <div className="p-5 rounded-sm" style={{ border: '1px solid #D4C9B8' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[13px] font-medium" style={{ color: '#3C3226' }}>Sentiment Overview</h3>
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-none" style={{
                backgroundColor: summary.avg_score >= 7 ? 'rgba(29,158,117,0.06)' : summary.avg_score >= 4 ? 'rgba(229,160,0,0.06)' : 'rgba(212,68,68,0.06)',
              }}>
                {summary.avg_score >= 7 ? <Smile size={14} style={{ color: '#1D6B4F' }} /> :
                 summary.avg_score >= 4 ? <Meh size={14} style={{ color: '#E5A000' }} /> :
                 <Frown size={14} style={{ color: '#D44' }} />}
                <span className="text-[13px] font-semibold" style={{
                  color: summary.avg_score >= 7 ? '#1D6B4F' : summary.avg_score >= 4 ? '#E5A000' : '#D44'
                }}>{summary.avg_score}/10</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Positive', count: summary.positive, color: '#1D6B4F', icon: Smile },
                { label: 'Neutral', count: summary.neutral, color: '#E5A000', icon: Meh },
                { label: 'Negative', count: summary.negative, color: '#D44', icon: Frown },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="p-3 rounded-none text-center" style={{ backgroundColor: '#F7F4EF' }}>
                    <Icon size={18} className="mx-auto mb-1" style={{ color: s.color }} />
                    <div className="text-lg font-semibold" style={{ color: s.color }}>{s.count || 0}</div>
                    <div className="text-[10px]" style={{ color: '#5C5347' }}>{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      <button
        onClick={runAnalysis}
        disabled={analyzing}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-none text-[#F0EBE3] text-sm font-medium mb-4 transition-opacity hover:opacity-85 disabled:opacity-60"
        style={{ backgroundColor: '#2C2520' }}
        data-testid="run-sentiment-btn"
      >
        {analyzing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
        {analyzing ? 'Analyzing sentiment...' : data?.has_report ? 'Re-analyze' : 'Analyze AI sentiment'}
      </button>

      {/* Mentions */}
      {mentions.length > 0 && (
        <div className="space-y-2">
          {mentions.map((m, i) => (
            <div key={i} className="p-4 rounded-sm" style={{ border: '1px solid #D4C9B8' }}>
              <div className="flex items-center gap-2 mb-2">
                {m.sentiment === 'positive' ? <Smile size={14} style={{ color: '#1D6B4F' }} /> :
                 m.sentiment === 'negative' ? <Frown size={14} style={{ color: '#D44' }} /> :
                 <Meh size={14} style={{ color: '#E5A000' }} />}
                <span className="text-[11px] font-medium uppercase" style={{
                  color: m.sentiment === 'positive' ? '#1D6B4F' : m.sentiment === 'negative' ? '#D44' : '#E5A000'
                }}>{m.sentiment}</span>
                <span className="text-[11px]" style={{ color: '#8C7E6E' }}>{m.engine} &middot; {m.score}/10</span>
              </div>
              {m.highlight && <p className="text-[13px] font-medium mb-1" style={{ color: '#3C3226' }}>"{m.highlight}"</p>}
              <p className="text-[12px]" style={{ color: '#5C5347' }}>{m.reason}</p>
            </div>
          ))}
        </div>
      )}

      {!data?.has_report && !analyzing && (
        <div className="py-12 text-center">
          <Smile size={48} className="mx-auto mb-4" style={{ color: '#D4C9B8' }} strokeWidth={1} />
          <h3 className="text-lg font-medium mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>How does AI feel about you?</h3>
          <p className="text-[13px] max-w-sm mx-auto" style={{ color: '#5C5347' }}>Analyze the tone and sentiment of every AI mention of your business.</p>
        </div>
      )}
    </div>
  );
}


function RobotsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/robots-check`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: '#D4C9B8' }} /></div>;

  if (!data?.has_website) {
    return (
      <div className="py-12 text-center" data-testid="robots-no-website">
        <Globe size={48} className="mx-auto mb-4" style={{ color: '#D4C9B8' }} strokeWidth={1} />
        <p className="text-[13px]" style={{ color: '#5C5347' }}>Add your website URL in your profile to check AI crawler access.</p>
      </div>
    );
  }

  const results = data?.results || [];
  const blockedCount = data?.blocked_count || 0;

  return (
    <div data-testid="robots-tab">
      {/* Status Banner */}
      <div className="p-4 rounded-sm mb-4" style={{
        backgroundColor: blockedCount > 0 ? 'rgba(212,68,68,0.04)' : 'rgba(29,158,117,0.04)',
        border: '1px solid #D4C9B8',
      }}>
        <div className="flex items-center gap-2 mb-1">
          {blockedCount > 0 ? <AlertTriangle size={16} style={{ color: '#D44' }} /> : <ShieldCheck size={16} style={{ color: '#1D6B4F' }} />}
          <span className="text-[13px] font-medium" style={{ color: blockedCount > 0 ? '#D44' : '#1D6B4F' }}>
            {blockedCount > 0 ? `${blockedCount} AI crawler${blockedCount > 1 ? 's' : ''} blocked` : 'All AI crawlers allowed'}
          </span>
        </div>
        <p className="text-[12px] ml-6" style={{ color: '#5C5347' }}>{data?.recommendation}</p>
      </div>

      {/* Crawler list */}
      <div className="space-y-2">
        {results.map((r, i) => (
          <div key={i} className="p-4 rounded-sm flex items-center gap-3" style={{ border: '1px solid #D4C9B8' }} data-testid={`crawler-${r.name}`}>
            <div className="w-8 h-8 rounded-none flex items-center justify-center" style={{
              backgroundColor: r.blocked ? 'rgba(212,68,68,0.06)' : 'rgba(29,158,117,0.06)'
            }}>
              {r.blocked ? <ShieldX size={16} style={{ color: '#D44' }} /> : <ShieldCheck size={16} style={{ color: '#1D6B4F' }} />}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[13px] font-medium block" style={{ color: '#3C3226' }}>{r.name}</span>
              <span className="text-[11px]" style={{ color: '#5C5347' }}>{r.owner} — {r.impact}</span>
            </div>
            <span className="text-[11px] font-medium px-2.5 py-1 rounded-full" style={{
              backgroundColor: r.blocked ? 'rgba(212,68,68,0.08)' : 'rgba(29,158,117,0.08)',
              color: r.blocked ? '#D44' : '#1D6B4F',
            }}>
              {r.blocked ? 'Blocked' : 'Allowed'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


function LlmsTxtTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/llms-txt`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/llms-txt/generate`, {}, { withCredentials: true });
      setData(res.data);
    } catch (e) {}
    setGenerating(false);
  };

  const copyContent = () => {
    navigator.clipboard.writeText(data?.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = () => {
    const blob = new Blob([data?.content || ''], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llms.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin" style={{ color: '#D4C9B8' }} /></div>;

  return (
    <div data-testid="llms-tab">
      <div className="p-4 rounded-sm mb-4" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
        <p className="text-[11px] font-medium uppercase tracking-wider mb-1" style={{ color: '#C67A5C' }}>What is llms.txt?</p>
        <p className="text-[12px]" style={{ color: '#5C5347' }}>
          A structured text file you add to your website (yoursite.com/llms.txt) that helps AI models understand your business — like robots.txt but for LLMs.
        </p>
      </div>

      <button
        onClick={generate}
        disabled={generating}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-none text-[#F0EBE3] text-sm font-medium mb-4 transition-opacity hover:opacity-85 disabled:opacity-60"
        style={{ backgroundColor: '#2C2520' }}
        data-testid="generate-llms-txt-btn"
      >
        {generating ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
        {generating ? 'Generating...' : data?.has_content ? 'Regenerate' : 'Generate your llms.txt'}
      </button>

      {data?.has_content && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex gap-2 mb-3">
            <button
              onClick={copyContent}
              className="flex items-center gap-1.5 px-4 py-2 rounded-none text-[12px] font-medium transition-colors hover:bg-[#EDE8DF]"
              style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}
              data-testid="copy-llms-txt"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={downloadFile}
              className="flex items-center gap-1.5 px-4 py-2 rounded-none text-[12px] font-medium transition-colors hover:bg-[#EDE8DF]"
              style={{ border: '1px solid #D4C9B8', color: '#3C3226' }}
              data-testid="download-llms-txt"
            >
              <Download size={13} /> Download
            </button>
          </div>
          <pre className="p-4 rounded-sm text-[12px] leading-relaxed whitespace-pre-wrap overflow-x-auto" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8', color: '#5C5347' }} data-testid="llms-txt-content">
            {data.content}
          </pre>
        </motion.div>
      )}

      {!data?.has_content && !generating && (
        <div className="py-12 text-center">
          <FileText size={48} className="mx-auto mb-4" style={{ color: '#D4C9B8' }} strokeWidth={1} />
          <h3 className="text-lg font-medium mb-2" style={{ fontFamily: "'DM Sans', sans-serif", color: '#3C3226' }}>Help AI understand your business</h3>
          <p className="text-[13px] max-w-sm mx-auto" style={{ color: '#5C5347' }}>Generate a structured llms.txt file to add to your website.</p>
        </div>
      )}
    </div>
  );
}
